/**
 * Skill Mismatch Tasks — HR → GM → MD escalation for wrong-skill
 * worker allocations.
 *
 * Route: /hr/skill-mismatch
 *
 * Two flavors of mismatch:
 *   OVER_SKILLED  — SKILLED placed on SEMI_SKILLED/TRAINEE slot. Cost waste.
 *   UNDER_SKILLED — TRAINEE placed on SKILLED slot. Quality/safety risk.
 *
 * Severity:
 *   HIGH   = UNDER_SKILLED on a critical-post slot. Urgent.
 *   MEDIUM = UNDER_SKILLED on non-critical, OR OVER_SKILLED by 2+ classes.
 *   LOW    = OVER_SKILLED by 1 class.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Button, Card, Col, DatePicker, Empty, Form, Input, Modal, Popconfirm,
  Row, Select, Space, Statistic, Table, Tag, Tooltip, Typography, message,
} from 'antd'
import {
  CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined,
  ReloadOutlined, ThunderboltOutlined, PlayCircleOutlined,
} from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'

import {
  MISMATCH_SEVERITIES, MISMATCH_STATUSES, MISMATCH_TYPES, SEVERITY_COLOR,
  SKILL_CLASS_COLOR, STATUS_COLOR, STATUS_LABEL, TYPE_COLOR,
  skillMismatchApi,
  type MismatchReasonCode, type MismatchSeverity, type MismatchStatus,
  type MismatchType, type SkillMismatchTask,
} from '@/features/hr/skillMismatchApi'

const { Title, Text } = Typography
const { TextArea } = Input

function ageSince(iso: string | null): string {
  if (!iso) return '—'
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 60_000) return 'just now'
  const mins = Math.floor(ms / 60_000)
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ${mins % 60}m`
  const days = Math.floor(hours / 24)
  return `${days}d ${hours % 24}h`
}

export default function SkillMismatchTasksPage() {
  const qc = useQueryClient()

  const [statusFilter, setStatusFilter] = useState<MismatchStatus | 'OPEN' | 'ALL'>('OPEN')
  const [severityFilter, setSeverityFilter] = useState<MismatchSeverity | undefined>()
  const [typeFilter, setTypeFilter] = useState<MismatchType | undefined>()
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null)
  const [sweepDate, setSweepDate] = useState<Dayjs>(dayjs())
  const [actionModal, setActionModal] = useState<SkillMismatchTask | null>(null)
  const [actionForm] = Form.useForm<{
    reason_code: MismatchReasonCode; reason_notes?: string; waive?: boolean
  }>()

  const summaryQ = useQuery({
    queryKey: ['skill-mismatch-summary'],
    queryFn: skillMismatchApi.summary,
    refetchInterval: 60_000,
  })

  const listParams = useMemo(() => {
    const p: Parameters<typeof skillMismatchApi.list>[0] = { limit: 500 }
    if (statusFilter !== 'OPEN' && statusFilter !== 'ALL') p.status = statusFilter
    if (severityFilter) p.severity = severityFilter
    if (typeFilter) p.mismatch_type = typeFilter
    if (dateRange?.[0]) p.on_date_from = dateRange[0].format('YYYY-MM-DD')
    if (dateRange?.[1]) p.on_date_to = dateRange[1].format('YYYY-MM-DD')
    return p
  }, [statusFilter, severityFilter, typeFilter, dateRange])

  const tasksQ = useQuery({
    queryKey: ['skill-mismatch-tasks', listParams],
    queryFn: () => skillMismatchApi.list(listParams),
  })

  const reasonCodesQ = useQuery({
    queryKey: ['skill-mismatch-reason-codes'],
    queryFn: skillMismatchApi.reasonCodes,
    staleTime: Infinity,
  })

  const filteredTasks = useMemo(() => {
    const all = tasksQ.data ?? []
    if (statusFilter === 'OPEN') {
      return all.filter((t) =>
        t.status === 'PENDING_HR' || t.status === 'ESCALATED_GM' || t.status === 'ESCALATED_MD'
      )
    }
    return all
  }, [tasksQ.data, statusFilter])

  // ── Mutations ──
  const sweepMut = useMutation({
    mutationFn: () => skillMismatchApi.sweep(sweepDate.format('YYYY-MM-DD')),
    onSuccess: (r) => {
      message.success(
        `Swept ${r.allocations_checked} allocations on ${r.on_date}. ` +
        `Created ${r.created}, skipped ${r.skipped_class_matches} (matches), ` +
        `${r.skipped_existing_task} (already flagged), ` +
        `${r.skipped_no_required_class} (no required class).`,
        10,
      )
      qc.invalidateQueries({ queryKey: ['skill-mismatch-tasks'] })
      qc.invalidateQueries({ queryKey: ['skill-mismatch-summary'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Sweep failed'),
  })

  const escalateMut = useMutation({
    mutationFn: skillMismatchApi.escalatePending,
    onSuccess: (r) => {
      message.success(`Promoted ${r.promoted_to_gm} → GM, ${r.promoted_to_md} → MD. ${r.total_open} open.`)
      qc.invalidateQueries({ queryKey: ['skill-mismatch-tasks'] })
      qc.invalidateQueries({ queryKey: ['skill-mismatch-summary'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Escalate failed'),
  })

  const actionMut = useMutation({
    mutationFn: (v: { id: string; body: Parameters<typeof skillMismatchApi.action>[1] }) =>
      skillMismatchApi.action(v.id, v.body),
    onSuccess: () => {
      message.success('Task closed')
      setActionModal(null); actionForm.resetFields()
      qc.invalidateQueries({ queryKey: ['skill-mismatch-tasks'] })
      qc.invalidateQueries({ queryKey: ['skill-mismatch-summary'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Action failed'),
  })

  const gmAckMut = useMutation({
    mutationFn: skillMismatchApi.gmAcknowledge,
    onSuccess: () => {
      message.success('GM acknowledged')
      qc.invalidateQueries({ queryKey: ['skill-mismatch-tasks'] })
    },
  })
  const mdAckMut = useMutation({
    mutationFn: skillMismatchApi.mdAcknowledge,
    onSuccess: () => {
      message.success('MD acknowledged')
      qc.invalidateQueries({ queryKey: ['skill-mismatch-tasks'] })
    },
  })

  const submitAction = (waive: boolean) => {
    actionForm.validateFields().then((vals) => {
      if (!actionModal) return
      actionMut.mutate({
        id: actionModal.id,
        body: { reason_code: vals.reason_code, reason_notes: vals.reason_notes, waive },
      })
    })
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 4 }}>Skill Mismatch Tasks</Title>
      <Text type="secondary">
        Workers allocated to slots where their skill class doesn't match the requirement.
        <Tag color="gold" style={{ marginLeft: 8 }}>OVER_SKILLED</Tag>= cost waste.
        <Tag color="red">UNDER_SKILLED</Tag>= quality risk. Same HR→GM→MD escalation as variance tasks.
      </Text>

      {/* Summary widgets */}
      <Row gutter={16} style={{ marginTop: 16, marginBottom: 16 }}>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic title="Pending HR" value={summaryQ.data?.pending_hr ?? '—'}
              valueStyle={{ color: '#d4b106' }} prefix={<ClockCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic title="Escalated GM" value={summaryQ.data?.escalated_gm ?? '—'}
              valueStyle={{ color: '#fa8c16' }} prefix={<ExclamationCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic title="Escalated MD" value={summaryQ.data?.escalated_md ?? '—'}
              valueStyle={{ color: '#f5222d' }} prefix={<ExclamationCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic title="HIGH severity (open)"
              value={summaryQ.data?.by_severity_open?.HIGH ?? 0}
              valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic title="Resolved" value={summaryQ.data?.resolved ?? '—'}
              valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic title="Total Open" value={summaryQ.data?.total_open ?? '—'}
              valueStyle={{ color: '#1677ff' }} />
          </Card>
        </Col>
      </Row>

      {/* Generator + escalation actions */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Text strong>Sweep allocations for:</Text>
          <DatePicker value={sweepDate} onChange={(d) => d && setSweepDate(d)}
            allowClear={false} disabledDate={(d) => d.isAfter(dayjs(), 'day')} />
          <Popconfirm
            title={`Run skill mismatch sweep for ${sweepDate.format('DD MMM YYYY')}?`}
            description="Scans every active allocation and creates missing mismatch tasks. Idempotent."
            onConfirm={() => sweepMut.mutate()}
            okText="Sweep"
          >
            <Button type="primary" icon={<PlayCircleOutlined />}
              loading={sweepMut.isPending}>
              Run Sweep
            </Button>
          </Popconfirm>
          <Tooltip title="PENDING_HR → GM after 24h, GM → MD after 48h.">
            <Popconfirm
              title="Tick the escalation state machine now?"
              onConfirm={() => escalateMut.mutate()}
              okText="Run"
            >
              <Button icon={<ThunderboltOutlined />} loading={escalateMut.isPending}>
                Run Escalation
              </Button>
            </Popconfirm>
          </Tooltip>
        </Space>
      </Card>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Text>Status:</Text>
          <Select value={statusFilter} onChange={(v) => setStatusFilter(v)}
            style={{ width: 200 }}
            options={[
              { label: 'Open (HR + GM + MD)', value: 'OPEN' },
              { label: 'All statuses', value: 'ALL' },
              ...MISMATCH_STATUSES.map((s) => ({ label: STATUS_LABEL[s], value: s })),
            ]} />
          <Text>Severity:</Text>
          <Select allowClear placeholder="any" value={severityFilter}
            onChange={setSeverityFilter} style={{ width: 130 }}
            options={MISMATCH_SEVERITIES.map((s) => ({ label: s, value: s }))} />
          <Text>Type:</Text>
          <Select allowClear placeholder="any" value={typeFilter}
            onChange={setTypeFilter} style={{ width: 180 }}
            options={MISMATCH_TYPES.map((t) => ({ label: t, value: t }))} />
          <DatePicker.RangePicker value={dateRange as any}
            onChange={(r) => setDateRange(r as any)} />
          <Button icon={<ReloadOutlined />}
            onClick={() => qc.invalidateQueries({ queryKey: ['skill-mismatch-tasks'] })}>
            Refresh
          </Button>
        </Space>
      </Card>

      <Card>
        {filteredTasks.length === 0 && !tasksQ.isFetching ? (
          <Empty
            description={
              statusFilter === 'OPEN'
                ? 'No open skill mismatch tasks. Either every allocation matches, or HR has closed them all.'
                : 'No tasks match the current filter.'
            }
          />
        ) : (
          <Table<SkillMismatchTask>
            dataSource={filteredTasks} rowKey="id"
            loading={tasksQ.isLoading}
            pagination={{ pageSize: 50, showSizeChanger: true }}
            size="small"
            columns={[
              {
                title: 'Date', dataIndex: 'on_date', width: 100,
                sorter: (a, b) => (a.on_date ?? '').localeCompare(b.on_date ?? ''),
                defaultSortOrder: 'descend',
                render: (d) => d ? dayjs(d).format('DD MMM') : '—',
              },
              {
                title: 'Employee', key: 'emp', width: 240,
                render: (_, t) => (
                  <Space direction="vertical" size={0}>
                    <Text strong>{t.emp_name ?? '—'}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t.emp_code ?? 'no code'} · {t.department_name ?? 'no dept'}
                    </Text>
                  </Space>
                ),
              },
              {
                title: 'Role', dataIndex: 'required_role', width: 140,
                render: (v) => v ?? '—',
              },
              {
                title: 'Required vs Actual', key: 'cls', width: 220,
                render: (_, t) => (
                  <Space>
                    <Tooltip title="What the slot requires">
                      <Tag color={SKILL_CLASS_COLOR[t.required_skill_class]}>
                        Req: {t.required_skill_class}
                      </Tag>
                    </Tooltip>
                    <Text style={{ fontSize: 12, color: '#8c8c8c' }}>→</Text>
                    <Tooltip title="What was actually allocated">
                      <Tag color={SKILL_CLASS_COLOR[t.actual_skill_class]}>
                        Got: {t.actual_skill_class}
                      </Tag>
                    </Tooltip>
                  </Space>
                ),
              },
              {
                title: 'Type', dataIndex: 'mismatch_type', width: 130,
                filters: MISMATCH_TYPES.map((t) => ({ text: t, value: t })),
                onFilter: (v, r) => r.mismatch_type === v,
                render: (v: MismatchType) => <Tag color={TYPE_COLOR[v]}>{v}</Tag>,
              },
              {
                title: 'Severity', dataIndex: 'severity', width: 100,
                filters: MISMATCH_SEVERITIES.map((s) => ({ text: s, value: s })),
                onFilter: (v, r) => r.severity === v,
                render: (v: MismatchSeverity, r) => (
                  <Space direction="vertical" size={0}>
                    <Tag color={SEVERITY_COLOR[v]}>{v}</Tag>
                    {r.critical_post_flag && (
                      <Text type="danger" style={{ fontSize: 10 }}>critical post</Text>
                    )}
                  </Space>
                ),
              },
              {
                title: 'Status', dataIndex: 'status', width: 140,
                render: (s: MismatchStatus, t) => (
                  <Space direction="vertical" size={0}>
                    <Tag color={STATUS_COLOR[s]}>{STATUS_LABEL[s]}</Tag>
                    {t.gm_acknowledged_at && <Tag color="orange" style={{ fontSize: 10 }}>GM ✓</Tag>}
                    {t.md_acknowledged_at && <Tag color="red" style={{ fontSize: 10 }}>MD ✓</Tag>}
                  </Space>
                ),
              },
              {
                title: 'Age', key: 'age', width: 90,
                sorter: (a, b) =>
                  new Date(a.created_at ?? 0).getTime() - new Date(b.created_at ?? 0).getTime(),
                render: (_, t) => (
                  <Tooltip title={t.created_at ? dayjs(t.created_at).format('DD MMM HH:mm') : ''}>
                    <Text type={ageSince(t.created_at).includes('d') ? 'danger' : undefined}>
                      {ageSince(t.created_at)}
                    </Text>
                  </Tooltip>
                ),
              },
              {
                title: 'Reason', key: 'reason', width: 180,
                render: (_, t) => t.reason_code ? (
                  <Tooltip title={t.reason_notes ?? ''}>
                    <Tag>{t.reason_code}</Tag>
                  </Tooltip>
                ) : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>,
              },
              {
                title: 'Action', key: 'action', fixed: 'right', width: 180,
                render: (_, t) => {
                  const isOpen = t.status === 'PENDING_HR'
                    || t.status === 'ESCALATED_GM'
                    || t.status === 'ESCALATED_MD'
                  return (
                    <Space size={4}>
                      {isOpen && (
                        <Button size="small" type="primary"
                          onClick={() => { setActionModal(t); actionForm.resetFields() }}>
                          Action
                        </Button>
                      )}
                      {t.status === 'ESCALATED_GM' && !t.gm_acknowledged_at && (
                        <Button size="small" onClick={() => gmAckMut.mutate(t.id)}>
                          GM Ack
                        </Button>
                      )}
                      {t.status === 'ESCALATED_MD' && !t.md_acknowledged_at && (
                        <Button size="small" danger onClick={() => mdAckMut.mutate(t.id)}>
                          MD Ack
                        </Button>
                      )}
                    </Space>
                  )
                },
              },
            ]}
            scroll={{ x: 1400 }}
          />
        )}
      </Card>

      <Modal
        open={!!actionModal}
        title={
          actionModal ? `Capture reason — ${actionModal.emp_name} (${actionModal.emp_code ?? 'no code'})` : ''
        }
        onCancel={() => { setActionModal(null); actionForm.resetFields() }}
        footer={[
          <Button key="cancel" onClick={() => { setActionModal(null); actionForm.resetFields() }}>
            Cancel
          </Button>,
          <Button key="waive" onClick={() => submitAction(true)} loading={actionMut.isPending}>
            Waive (no action needed)
          </Button>,
          <Button key="resolve" type="primary" onClick={() => submitAction(false)} loading={actionMut.isPending}>
            Resolve
          </Button>,
        ]}
        destroyOnClose
      >
        {actionModal && (
          <>
            <Alert
              type={actionModal.mismatch_type === 'UNDER_SKILLED' ? 'warning' : 'info'}
              showIcon
              message={
                <>
                  <Tag color={TYPE_COLOR[actionModal.mismatch_type]}>{actionModal.mismatch_type}</Tag>
                  Required: <b>{actionModal.required_skill_class}</b>,
                  got: <b>{actionModal.actual_skill_class}</b>.
                  {actionModal.critical_post_flag && (
                    <Tag color="red" style={{ marginLeft: 8 }}>Critical post</Tag>
                  )}
                </>
              }
              style={{ marginBottom: 16 }}
            />
            <Form form={actionForm} layout="vertical">
              <Form.Item name="reason_code" label="Reason"
                rules={[{ required: true, message: 'Pick a reason' }]}>
                <Select placeholder="Why is this mismatch acceptable / how will you fix it?"
                  options={(reasonCodesQ.data?.codes ?? []).map((c) => ({
                    label: reasonCodesQ.data?.labels[c] ?? c,
                    value: c,
                  }))} />
              </Form.Item>
              <Form.Item name="reason_notes" label="Notes">
                <TextArea rows={3}
                  placeholder="e.g. No SKILLED operator on shift today, will rotate by Wed." />
              </Form.Item>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <strong>Resolve</strong> = reason captured, ticket closed.
                {' '}<strong>Waive</strong> = no action needed (e.g. permanent override approved).
              </Text>
            </Form>
          </>
        )}
      </Modal>
    </div>
  )
}
